/**
 * Onboarding des clients PME — règles d'AFFICHAGE, pures et testées (migration 0034).
 *
 * L'autorité est la base : transitions de statut, pièces exigées, offre complète, contrat publié,
 * signature… sont décidées par des déclencheurs. Ce module n'en rejoue que ce qu'il faut pour AVERTIR
 * avant l'appel (bouton grisé, message précis) et pour traduire les codes d'erreur en phrases.
 * Jamais de code brut à l'écran.
 */

import {
  identiteManque as cleIdentiteManque,
  libellesManquants,
  statutDe,
  type IdentiteJuridique,
  type StatutJuridique,
} from './designationPartie'
import { normaliserNumero } from './whatsapp'

export const STATUTS = ['prospect', 'documents', 'contrat_envoye', 'contrat_signe', 'actif', 'refuse', 'suspendu'] as const
export type StatutOnboarding = (typeof STATUTS)[number]

/** Les cinq étapes du parcours nominal (refus et suspension sont des sorties, pas des étapes). */
export const PARCOURS = ['prospect', 'documents', 'contrat_envoye', 'contrat_signe', 'actif'] as const

export const LIBELLE_STATUT: Record<StatutOnboarding, string> = {
  prospect: 'Prospect',
  documents: 'Pièces',
  contrat_envoye: 'Contrat envoyé',
  contrat_signe: 'Contrat signé',
  actif: 'Actif',
  refuse: 'Refusé',
  suspendu: 'Suspendu',
}

/** Classes de badge existantes de la console (`.b-*`). */
export const COULEUR_STATUT: Record<StatutOnboarding, string> = {
  prospect: 'b-gray',
  documents: 'b-blue',
  contrat_envoye: 'b-warn',
  contrat_signe: 'b-gold',
  actif: 'b-green',
  refuse: 'b-red',
  suspendu: 'b-red',
}

export function estStatut(v: unknown): v is StatutOnboarding {
  return typeof v === 'string' && (STATUTS as readonly string[]).includes(v)
}

/* ---------------------------------------------------------------- pièces */

export type KindPiece = 'rccm' | 'nif' | 'identite_gerant' | 'declaration_entreprenant' | 'mandat_signataire' | 'autre'
export const KINDS_PIECE: readonly KindPiece[] = ['rccm', 'nif', 'identite_gerant', 'declaration_entreprenant', 'mandat_signataire', 'autre']

/**
 * Pièces dont la base exige la VÉRIFICATION avant l'envoi du contrat, SELON LE STATUT JURIDIQUE (0035,
 * `client_pieces_requises`). Statut non choisi : la pièce commune à tous, l'identité.
 *   société → RCCM, NIF, identité du représentant ; entreprenant → déclaration d'entreprenant, identité ;
 *   personne physique → identité.
 */
export function piecesRequises(statut: StatutJuridique | null): readonly KindPiece[] {
  if (statut === 'societe') return ['rccm', 'nif', 'identite_gerant']
  if (statut === 'entreprenant') return ['declaration_entreprenant', 'identite_gerant']
  return ['identite_gerant']
}

/** Types proposés au dépôt : les exigés du statut, puis les compléments utiles (NIF d'un entreprenant, mandat…). */
export function kindsProposes(statut: StatutJuridique | null): readonly KindPiece[] {
  if (statut === 'societe') return ['rccm', 'nif', 'identite_gerant', 'mandat_signataire', 'autre']
  if (statut === 'entreprenant') return ['declaration_entreprenant', 'identite_gerant', 'nif', 'autre']
  if (statut === 'personne_physique') return ['identite_gerant', 'autre']
  return ['identite_gerant', 'autre']
}

/** `identite_gerant` désigne la pièce d'identité du représentant (société) ou de la personne elle-même. */
export function libellePiece(kind: KindPiece, statut: StatutJuridique | null): string {
  if (kind === 'identite_gerant') return statut === 'societe' ? 'Pièce d’identité du représentant légal' : 'Pièce d’identité'
  return LIBELLE_PIECE[kind]
}

export const LIBELLE_PIECE: Record<KindPiece, string> = {
  rccm: 'Extrait RCCM',
  nif: 'NIF (carte ou attestation)',
  identite_gerant: 'Pièce d’identité',
  declaration_entreprenant: 'Déclaration d’entreprenant',
  mandat_signataire: 'Mandat du signataire',
  autre: 'Autre pièce (ex. contrat papier scanné)',
}

export type StatutPiece = 'valide' | 'rejete' | 'en_attente'
export interface PieceLigne { kind: KindPiece; is_verified: boolean; rejected_reason: string | null }

export function statutPiece(p: Pick<PieceLigne, 'is_verified' | 'rejected_reason'>): StatutPiece {
  if (p.is_verified) return 'valide'
  return p.rejected_reason ? 'rejete' : 'en_attente'
}
export const LIBELLE_STATUT_PIECE: Record<StatutPiece, string> = { valide: 'Validée', rejete: 'Rejetée', en_attente: 'À examiner' }
export const COULEUR_PIECE: Record<StatutPiece, string> = { valide: 'b-green', rejete: 'b-red', en_attente: 'b-warn' }

/** Types exigés (pour ce statut) qui n'ont AUCUNE pièce validée. */
export function piecesManquantes(pieces: readonly PieceLigne[], statut: StatutJuridique | null): KindPiece[] {
  return piecesRequises(statut).filter((k) => !pieces.some((p) => p.kind === k && p.is_verified))
}

export const TAILLE_MAX_PIECE = 8 * 1024 * 1024
export const MIME_PIECES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'application/pdf'] as const

/** Contrôle de confort avant l'envoi au bucket (la base et le bucket refusent de toute façon). */
export function fichierRecevable(f: { type: string; size: number }): string | null {
  if (!(MIME_PIECES as readonly string[]).includes(f.type)) return 'Format refusé : PDF, JPEG, PNG, WebP ou HEIC seulement.'
  if (f.size <= 0) return 'Le fichier est vide.'
  if (f.size > TAILLE_MAX_PIECE) return 'Fichier trop lourd : 8 Mo au maximum.'
  return null
}

/** Chemin dans le bucket privé : <org>/clients/<client>/<horodatage>-<kind>-<nom nettoyé>. */
export function cheminPiece(orgId: string, clientId: string, kind: KindPiece, nomBrut: string, maintenant = Date.now()): string {
  /* Seul le dernier segment compte : jamais de remontée de dossier (« ../ ») dans un chemin de bucket. */
  const nom = nomBrut.split(/[\\/]/).pop() ?? ''
  const dot = nom.lastIndexOf('.')
  const ext = dot > 0 ? nom.slice(dot + 1).toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 5) : ''
  const base = (dot > 0 ? nom.slice(0, dot) : nom)
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'fichier'
  return `${orgId}/clients/${clientId}/${maintenant}-${kind}-${base}${ext ? `.${ext}` : ''}`
}

/* ---------------------------------------------------------------- offre */

export interface CreneauOffre { weekday: number; start_time: string; end_time: string; label?: string }
export type MoyenPaiement = 'wave' | 'orange_money' | 'virement'
export const MOYENS_PAIEMENT: readonly MoyenPaiement[] = ['wave', 'orange_money', 'virement']
export const LIBELLE_MOYEN: Record<MoyenPaiement, string> = { wave: 'Wave', orange_money: 'Orange Money', virement: 'Virement bancaire' }

export interface OffreChoisie {
  plan_code: string
  option_longue_distance: boolean
  creneaux: CreneauOffre[]
  supplement_hors_creneau_fcfa: number
  conditions_paiement: { moyens: MoyenPaiement[]; echeance_jours: number }
  prix_negocie_fcfa?: number | null
}

export interface PlanPme { code: string; name: string; price_fcfa: number | null; monthly_courses: number | null }
export interface GrilleMensuelle { option_longue_distance_mensuel_fcfa: number }

export const OFFRE_PAR_DEFAUT: OffreChoisie = {
  plan_code: 'pme_essentiel',
  option_longue_distance: false,
  creneaux: [{ weekday: 1, start_time: '09:00', end_time: '11:00' }],
  supplement_hors_creneau_fcfa: 500,
  conditions_paiement: { moyens: ['wave', 'orange_money'], echeance_jours: 15 },
}

const HEURE = /^([01][0-9]|2[0-3]):[0-5][0-9]$/
const entier = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v)

/**
 * JUMELLE TypeScript de `client_offre_manque()` (migration 0034) : les clés manquantes ou invalides.
 * `plans` = le catalogue PME (`managed_plans`). La base fait foi ; ceci évite un aller-retour inutile.
 */
export function offreManque(o: unknown, plans: readonly PlanPme[]): string[] {
  if (!o || typeof o !== 'object' || Array.isArray(o)) {
    return ['plan_code', 'option_longue_distance', 'creneaux', 'supplement_hors_creneau_fcfa', 'conditions_paiement']
  }
  const p = o as Record<string, unknown>
  const m: string[] = []
  const plan = typeof p.plan_code === 'string' ? plans.find((x) => x.code === p.plan_code) : undefined
  if (!plan) m.push('plan_code')
  if (typeof p.option_longue_distance !== 'boolean') m.push('option_longue_distance')

  const cr = p.creneaux
  if (!Array.isArray(cr) || cr.length < 1) m.push('creneaux')
  else if (
    !cr.every((c) => {
      if (!c || typeof c !== 'object') return false
      const x = c as Record<string, unknown>
      return (
        entier(x.weekday) && x.weekday >= 1 && x.weekday <= 7 &&
        typeof x.start_time === 'string' && HEURE.test(x.start_time) &&
        typeof x.end_time === 'string' && HEURE.test(x.end_time) && x.start_time < x.end_time
      )
    })
  ) m.push('creneaux')

  if (!entier(p.supplement_hors_creneau_fcfa) || p.supplement_hors_creneau_fcfa < 0) m.push('supplement_hors_creneau_fcfa')

  const cp = p.conditions_paiement as Record<string, unknown> | undefined
  const moyensOk =
    !!cp && typeof cp === 'object' && Array.isArray(cp.moyens) && cp.moyens.length >= 1 &&
    cp.moyens.every((x) => typeof x === 'string' && (MOYENS_PAIEMENT as readonly string[]).includes(x)) &&
    entier(cp.echeance_jours) && cp.echeance_jours >= 0 && cp.echeance_jours <= 120
  if (!moyensOk) m.push('conditions_paiement')

  const prix = p.prix_negocie_fcfa
  if (typeof prix === 'number') {
    if (!entier(prix) || prix <= 0) m.push('prix_negocie_fcfa')
  } else if (plan && plan.price_fcfa === null) {
    m.push('prix_negocie_fcfa')
  } else if (prix !== undefined && prix !== null) {
    m.push('prix_negocie_fcfa')
  }
  return m
}

export const LIBELLE_CLE_OFFRE: Record<string, string> = {
  plan_code: 'le forfait',
  option_longue_distance: 'l’option longue distance',
  creneaux: 'au moins un créneau valide',
  supplement_hors_creneau_fcfa: 'le supplément hors créneau',
  conditions_paiement: 'les conditions de paiement (moyens et échéance)',
  prix_negocie_fcfa: 'le prix négocié (forfait sur devis)',
}

/** Aperçu mensuel : forfait (prix négocié prioritaire) + option longue distance. `null` si pas de prix. */
export function apercuMensuel(
  o: Pick<OffreChoisie, 'option_longue_distance' | 'prix_negocie_fcfa'>,
  plan: PlanPme | undefined,
  grille: GrilleMensuelle | null,
): { forfait: number; option: number; total: number; quota: number | null } | null {
  if (!plan) return null
  const forfait = typeof o.prix_negocie_fcfa === 'number' && o.prix_negocie_fcfa > 0 ? o.prix_negocie_fcfa : plan.price_fcfa
  if (forfait === null) return null
  const option = o.option_longue_distance ? (grille?.option_longue_distance_mensuel_fcfa ?? 0) : 0
  return { forfait, option, total: forfait + option, quota: plan.monthly_courses }
}

/** Brouillon d'offre depuis la fiche (forfait, option, supplément et créneaux déjà saisis à la création). */
export function offreDepuisFiche(
  f: { plan_code: string | null; option_longue_distance: boolean; offslot_surcharge_fcfa: number; slots: readonly { weekday: number; start_time: string; end_time: string }[] },
  plans: readonly PlanPme[],
): OffreChoisie {
  const plan = plans.find((p) => p.code === f.plan_code)?.code ?? OFFRE_PAR_DEFAUT.plan_code
  const hhmm = (t: string) => t.slice(0, 5)
  return {
    ...OFFRE_PAR_DEFAUT,
    plan_code: plan,
    option_longue_distance: f.option_longue_distance,
    supplement_hors_creneau_fcfa: f.offslot_surcharge_fcfa,
    creneaux: f.slots.length
      ? f.slots.map((s) => ({ weekday: s.weekday, start_time: hhmm(s.start_time), end_time: hhmm(s.end_time) }))
      : OFFRE_PAR_DEFAUT.creneaux.map((c) => ({ ...c })),
    conditions_paiement: { ...OFFRE_PAR_DEFAUT.conditions_paiement, moyens: [...OFFRE_PAR_DEFAUT.conditions_paiement.moyens] },
  }
}

/** Lit l'offre enregistrée (jsonb) sans lui faire confiance : champs inconnus ignorés. */
export function lireOffre(brut: unknown, repli: OffreChoisie): OffreChoisie {
  if (!brut || typeof brut !== 'object' || Array.isArray(brut)) return repli
  const p = brut as Record<string, unknown>
  const cp = (p.conditions_paiement ?? {}) as Record<string, unknown>
  return {
    plan_code: typeof p.plan_code === 'string' ? p.plan_code : repli.plan_code,
    option_longue_distance: typeof p.option_longue_distance === 'boolean' ? p.option_longue_distance : repli.option_longue_distance,
    creneaux: Array.isArray(p.creneaux)
      ? p.creneaux.filter((c): c is CreneauOffre => !!c && typeof c === 'object').map((c) => ({
          weekday: Number((c as CreneauOffre).weekday),
          start_time: String((c as CreneauOffre).start_time ?? ''),
          end_time: String((c as CreneauOffre).end_time ?? ''),
          ...((c as CreneauOffre).label ? { label: String((c as CreneauOffre).label) } : {}),
        }))
      : repli.creneaux,
    supplement_hors_creneau_fcfa: typeof p.supplement_hors_creneau_fcfa === 'number' ? p.supplement_hors_creneau_fcfa : repli.supplement_hors_creneau_fcfa,
    conditions_paiement: {
      moyens: Array.isArray(cp.moyens) ? (cp.moyens.filter((x) => (MOYENS_PAIEMENT as readonly string[]).includes(String(x))) as MoyenPaiement[]) : repli.conditions_paiement.moyens,
      echeance_jours: typeof cp.echeance_jours === 'number' ? cp.echeance_jours : repli.conditions_paiement.echeance_jours,
    },
    ...(typeof p.prix_negocie_fcfa === 'number' ? { prix_negocie_fcfa: p.prix_negocie_fcfa } : {}),
  }
}

/* ---------------------------------------------------------------- identité */

/**
 * L'identité juridique d'une fiche. Les règles (champs exigés selon le statut) et la désignation de la partie au
 * contrat vivent dans `designationPartie.ts`, copie identique du module de l'edge function.
 */
export type IdentiteLigne = IdentiteJuridique
export type { StatutJuridique }
export { statutDe }

/** Capital saisi (« 1 000 000 », « 1000000 ») → entier, ou null s'il est vide ; texte illisible = erreur claire. */
export function lireCapital(brut: string): number | null {
  const net = brut.replace(/[\s\u00a0\u202f]/g, '')
  if (net === '') return null
  if (!/^\d+$/.test(net)) throw new Error('Le capital est un nombre entier de francs (ex. 1 000 000).')
  return Number(net)
}

/** Champs manquants, en français (« RCCM », « adresse du siège »…), pour le statut de la fiche. */
export function identiteManque(i: IdentiteLigne): string[] {
  return libellesManquants(cleIdentiteManque(i))
}

/* ---------------------------------------------------------------- parcours */

export interface Etape { cle: StatutOnboarding; libelle: string; etat: 'fait' | 'courant' | 'a_venir' | 'arret' }

/** Frise du parcours. Refusé/suspendu : on marque l'arrêt sur l'étape où le dossier s'est arrêté (inconnue : aucune). */
export function etapes(statut: StatutOnboarding): Etape[] {
  const idx = (PARCOURS as readonly string[]).indexOf(statut)
  return PARCOURS.map((cle, i) => ({
    cle,
    libelle: LIBELLE_STATUT[cle],
    etat: idx < 0 ? 'a_venir' : i < idx ? 'fait' : i === idx ? (cle === 'actif' ? 'fait' : 'courant') : 'a_venir',
  }))
}

export interface ContexteDossier {
  statut: StatutOnboarding
  piecesManquantes: readonly KindPiece[]
  identiteManque: readonly string[]
  offreManque: readonly string[]
  contratPublie: boolean
  signatureEnCours: boolean
  aSignatureSignee: boolean
  nbPieces: number
}

/** « À faire / En attente » : la prochaine chose utile, dite en une phrase. */
export function prochaineAction(c: ContexteDossier): { libelle: string; pourStaff: boolean } {
  switch (c.statut) {
    case 'prospect':
      return { libelle: 'choisir le statut juridique du client, puis déposer la première pièce du dossier.', pourStaff: true }
    case 'documents': {
      if (c.piecesManquantes.length) return { libelle: `faire valider : ${c.piecesManquantes.map((k) => LIBELLE_PIECE[k]).join(', ')}.`, pourStaff: true }
      if (c.identiteManque.length) return { libelle: `compléter l’identité : ${c.identiteManque.join(', ')}.`, pourStaff: true }
      if (c.offreManque.length) return { libelle: `arrêter l’offre : ${c.offreManque.map((k) => LIBELLE_CLE_OFFRE[k] ?? k).join(', ')}.`, pourStaff: true }
      if (!c.contratPublie) return { libelle: 'le propriétaire doit publier un contrat dans « Modèles de contrat ».', pourStaff: true }
      return { libelle: 'envoyer le contrat à signer (Documenso) ou enregistrer une acceptation manuelle.', pourStaff: true }
    }
    case 'contrat_envoye':
      return c.signatureEnCours
        ? { libelle: 'le gérant doit signer le contrat (lien envoyé).', pourStaff: false }
        : { libelle: 'envoyer le lien de signature, ou enregistrer l’acceptation manuelle (propriétaire).', pourStaff: true }
    case 'contrat_signe':
      return { libelle: 'le propriétaire peut activer le client.', pourStaff: true }
    case 'actif':
      return { libelle: 'client actif : les courses peuvent lui être créées.', pourStaff: false }
    case 'suspendu':
      return { libelle: 'client suspendu : le propriétaire peut le réactiver.', pourStaff: true }
    case 'refuse':
      return { libelle: 'dossier refusé : il peut être rouvert (retour à « Prospect »).', pourStaff: true }
  }
}

export interface ActionStatut { vers: StatutOnboarding; libelle: string; ton: 'principal' | 'neutre' | 'danger'; motifRequis: boolean; proprietaire: boolean }

/** Décisions proposées. `proprietaire` : réservé au propriétaire (activer, réactiver). La base le vérifie aussi. */
export function actionsStatut(statut: StatutOnboarding): ActionStatut[] {
  const refuser: ActionStatut = { vers: 'refuse', libelle: 'Refuser le dossier', ton: 'danger', motifRequis: true, proprietaire: false }
  const suspendre: ActionStatut = { vers: 'suspendu', libelle: 'Suspendre', ton: 'danger', motifRequis: true, proprietaire: false }
  switch (statut) {
    case 'prospect': return [refuser]
    case 'documents': return [suspendre, refuser]
    case 'contrat_envoye': return [{ vers: 'documents', libelle: 'Retirer le contrat (corriger le dossier)', ton: 'neutre', motifRequis: false, proprietaire: false }, suspendre, refuser]
    case 'contrat_signe': return [{ vers: 'actif', libelle: 'Activer le client', ton: 'principal', motifRequis: false, proprietaire: true }, suspendre, refuser]
    case 'actif': return [suspendre]
    case 'suspendu': return [{ vers: 'actif', libelle: 'Réactiver le client', ton: 'principal', motifRequis: false, proprietaire: true }]
    case 'refuse': return [{ vers: 'prospect', libelle: 'Rouvrir le dossier', ton: 'neutre', motifRequis: false, proprietaire: false }]
  }
}

/* ---------------------------------------------------------------- signature */

export interface SignatureLigne {
  id: string
  method: 'documenso' | 'manuelle'
  status: 'envoye' | 'signe' | 'refuse' | 'expire'
  template_version: number
  accepted_at: string | null
  created_at: string
  lien_signature: string | null
}
export const LIBELLE_METHODE: Record<SignatureLigne['method'], string> = { documenso: 'Signature électronique (Documenso)', manuelle: 'Acceptation manuelle (contrat papier)' }
export const LIBELLE_STATUT_SIGNATURE: Record<SignatureLigne['status'], string> = { envoye: 'Envoyé, en attente', signe: 'Signé', refuse: 'Refusé par le signataire', expire: 'Expiré' }
export const COULEUR_SIGNATURE: Record<SignatureLigne['status'], string> = { envoye: 'b-warn', signe: 'b-green', refuse: 'b-red', expire: 'b-gray' }

/** La signature à montrer : la signée la plus récente, à défaut la plus récente. */
export function signatureCourante<T extends SignatureLigne>(s: readonly T[]): T | null {
  const tri = [...s].sort((a, b) => b.created_at.localeCompare(a.created_at))
  return tri.find((x) => x.status === 'signe') ?? tri[0] ?? null
}
export function signatureEnCours<T extends SignatureLigne>(s: readonly T[]): T | null {
  return s.find((x) => x.method === 'documenso' && x.status === 'envoye') ?? null
}

/* ---------------------------------------------------------------- modèles de contrat */

/** Marques `{{…}}` reconnues par l'edge function (`contrat-variables.ts`) : à garder identiques. */
export const VARIABLES_CONTRAT = [
  'raison_sociale', 'rccm', 'nif', 'gerant', 'adresse', 'designation_partie', 'nom_partie', 'plan', 'prix_mensuel', 'quota_courses',
  'option_longue_distance', 'creneaux', 'supplement_hors_creneau', 'conditions_paiement',
  'moyens_paiement', 'echeance_paiement', 'date_du_jour',
] as const

/** Marques qui n'existent QUE pour une société : le contrat d'une personne physique ou d'un entreprenant les refuserait. */
export const MARQUES_SOCIETE_SEULEMENT = ['raison_sociale', 'rccm', 'gerant'] as const

export interface DiagnosticModele { marquesInconnues: string[]; passagesACompleter: number; marquesSocieteSeulement: string[] }

/** Alerte le juriste AVANT la publication : marques inconnues (bloqueraient chaque envoi) et crochets à compléter. */
export function diagnostiquerModele(corps: string): DiagnosticModele {
  const inconnues = new Set<string>()
  const societe = new Set<string>()
  for (const m of corps.matchAll(/\{\{\s*([a-zA-Z_]+)\s*\}\}/g)) {
    const nom = m[1] ?? ''
    if (!(VARIABLES_CONTRAT as readonly string[]).includes(nom)) inconnues.add(nom)
    else if ((MARQUES_SOCIETE_SEULEMENT as readonly string[]).includes(nom)) societe.add(nom)
  }
  const crochets = corps.match(/\[[^\]\n]{0,80}…[^\]\n]{0,80}\]|\[…\]/g) ?? []
  return { marquesInconnues: [...inconnues], passagesACompleter: crochets.length, marquesSocieteSeulement: [...societe] }
}

/* ---------------------------------------------------------------- WhatsApp */

export function messageSignature(nomGerant: string): string {
  const qui = nomGerant.trim() ? `Bonjour ${nomGerant.trim()}, ` : 'Bonjour, '
  return `${qui}voici le lien pour signer votre contrat de livraison SwiftDeliver :`
}

/**
 * Comment faire signer : e-mail (Documenso envoie le lien) ou WhatsApp (le lien est partagé par wa.me, sans API), deux
 * options ÉGALES. L'edge function accepte un client sans e-mail s'il a un numéro (adresse technique pour Documenso,
 * envoi d'e-mail forcé à « non ») ; elle refuse un client sans aucun des deux. Ici on dit AVANT l'appel quelle option
 * est possible, et pourquoi pas.
 */
export type MoyenEnvoiContrat = 'email' | 'whatsapp'

export interface OptionsEnvoiContrat {
  email: boolean
  whatsapp: boolean
  /** Option cochée d'office : l'e-mail s'il existe (usage habituel), sinon WhatsApp, sinon rien. */
  defaut: MoyenEnvoiContrat | null
  raisonEmail: string | null
  raisonWhatsapp: string | null
  /** Aucune des deux : l'envoi est impossible (une phrase), sinon null. */
  blocage: string | null
}

export function optionsEnvoiContrat(c: { email: string | null | undefined; phone: string | null | undefined }): OptionsEnvoiContrat {
  const email = (c.email ?? '').trim().length > 0
  const brut = (c.phone ?? '').trim()
  const whatsapp = normaliserNumero(brut) !== null
  const raisonEmail = email ? null : 'La fiche du client n’a pas d’adresse e-mail.'
  const raisonWhatsapp = whatsapp
    ? null
    : brut
      ? `Le numéro « ${brut} » est illisible : précisez l’indicatif du pays (ex. +223 75 08 36 64).`
      : 'La fiche du client n’a pas de numéro de téléphone.'
  return {
    email,
    whatsapp,
    defaut: email ? 'email' : whatsapp ? 'whatsapp' : null,
    raisonEmail,
    raisonWhatsapp,
    blocage: email || whatsapp ? null : 'Le client n’a ni adresse e-mail ni numéro WhatsApp exploitable : renseignez l’un des deux sur sa fiche.',
  }
}

/* ---------------------------------------------------------------- erreurs */

/**
 * Codes levés par la base (0034) et les edge functions Documenso, traduits en phrases. PostgREST renvoie le
 * code dans `error.message` ; l'edge function dans `error`/`detail`. Un code brut ne s'affiche JAMAIS.
 */
export const LISIBLE: Record<string, string> = {
  client_non_actif: 'Ce client PME n’a pas terminé son onboarding : aucune course ne peut lui être créée tant qu’il n’est pas actif.',
  transition_interdite: 'Ce changement de statut n’est pas permis depuis l’étape actuelle du dossier.',
  motif_requis: 'Un motif est obligatoire pour cette décision.',
  aucune_piece: 'Aucune pièce n’a été déposée.',
  dossier_incomplet: 'Le dossier est incomplet : les pièces exigées pour ce statut juridique doivent être déposées ET validées.',
  identite_incomplete: 'L’identité est incomplète pour le statut juridique choisi : la liste des champs manquants est affichée dans « Identité du client ».',
  offre_incomplete: 'L’offre choisie est incomplète : forfait, option, créneaux, supplément et conditions de paiement sont à arrêter.',
  offre_verrouillee: 'L’offre est figée depuis l’envoi du contrat : retirez le contrat pour la modifier.',
  identite_verrouillee: 'L’identité est figée depuis l’envoi du contrat : retirez le contrat pour la modifier.',
  aucun_contrat_publie: 'Aucun contrat n’est publié : le propriétaire doit en publier un dans « Modèles de contrat ».',
  contrat_non_signe: 'Le contrat n’est pas encore signé (pour sa version actuelle).',
  contrat_obsolete: 'Une version plus récente du contrat est publiée : rechargez la page.',
  contrat_non_publie: 'Ce contrat n’est pas publié.',
  contrat_introuvable: 'Contrat introuvable.',
  etape_non_atteinte: 'Le dossier n’a pas encore atteint cette étape.',
  owner_requis: 'Cette action est réservée au propriétaire.',
  note_obligatoire: 'Une note d’au moins 10 caractères est obligatoire pour une acceptation manuelle.',
  chemin_invalide: 'Le scan indiqué n’appartient pas au dossier de ce client.',
  client_particulier: 'Cette action ne concerne que les clients PME.',
  client_introuvable: 'Client introuvable.',
  dossier_refuse: 'Ce dossier est refusé : rouvrez-le avant d’y déposer une pièce.',
  signature_immuable: 'Une signature acquise ne se modifie pas.',
  contrat_publie_immuable: 'Une version publiée ne se modifie plus : créez un nouveau brouillon.',
  contrat_provisoire_non_publiable: 'Ce contrat est marqué « texte provisoire » : remplacez le texte puis décochez la case avant de publier.',
  contrat_publier_apres_creation: 'Créez d’abord le brouillon, relisez-le, puis publiez-le.',
  motif_rejet_requis: 'Indiquez le motif du rejet de la pièce.',
  non_authentifie: 'Session expirée — reconnectez-vous.',
  forbidden: 'Vous n’avez pas le droit d’effectuer cette action.',
  unauthorized: 'Session expirée — reconnectez-vous.',
  client_not_found: 'Client introuvable.',
  documenso_non_configure:
    'Documenso n’est pas encore configuré : les secrets DOCUMENSO_URL, DOCUMENSO_API_KEY et DOCUMENSO_WEBHOOK_SECRET restent à poser dans Supabase (voir docs/onboarding/ONBOARDING-PME.md). En attendant, utilisez l’acceptation manuelle (propriétaire).',
  documenso_erreur: 'Documenso n’a pas répondu correctement : réessayez dans un moment.',
  variables_manquantes: 'Le contrat ne peut pas être rendu : une valeur du dossier ou une marque du modèle est manquante.',
  missing_params: 'Information manquante pour cet envoi.',
  db_error: 'Erreur de la base de données : réessayez, ou contactez le support.',
}

const ESCAPE = /[.*+?^${}()|[\]\\]/g
const CODES = Object.keys(LISIBLE).sort((a, b) => b.length - a.length)

/** Le code connu contenu dans un message (mot entier), ou null. */
export function codeErreur(message: string): string | null {
  for (const code of CODES) {
    if (new RegExp(`(^|[^a-z0-9_])${code.replace(ESCAPE, '\\$&')}([^a-z0-9_]|$)`).test(message)) return code
  }
  return null
}

export function traduireErreur(message: string | null | undefined, detail?: string | null): string {
  const brut = (message ?? '').trim()
  if (!brut && !detail) return 'Opération impossible.'
  const code = codeErreur(brut)
  // Les erreurs de l'edge function portent parfois une phrase française utile dans `detail` (variables
  // manquantes, forfait introuvable…) : on la préfère au texte générique pour ces codes.
  if (code && detail && ['variables_manquantes', 'missing_params', 'offre_incomplete', 'identite_incomplete', 'documenso_erreur', 'db_error'].includes(code)) return detail
  if (code) return LISIBLE[code] ?? brut
  if (/row-level security/i.test(brut)) return 'Vous n’avez pas le droit d’effectuer cette action (règles de sécurité de la base).'
  if (/duplicate key|unique constraint/i.test(brut)) return 'Cet enregistrement existe déjà : rechargez la page.'
  if (/permission denied/i.test(brut)) return 'Vous n’avez pas le droit d’effectuer cette action.'
  if (/^[a-z][a-z0-9_]*$/.test(brut)) return 'L’opération a été refusée par la base.'
  return detail?.trim() || brut
}

export function messageErreur(e: unknown): string {
  if (e instanceof Error) return traduireErreur(e.message)
  if (typeof e === 'object' && e !== null && 'message' in e) return traduireErreur(String((e as { message: unknown }).message))
  return 'Opération impossible.'
}
